// src/pages/TrainingDetail.jsx
// Analisi/replay di una sessione di ALLENAMENTO salvata (dati statici).
// In alto si sceglie l'auto (o il pilota, nelle sessioni senza auto) e il
// pilota che l'ha guidata; tutto il resto della pagina riguarda solo quella
// scelta: traiettorie, giri, telemetria per-giro completa, settori, curve e
// il report PDF. Riusa i componenti grafici condivisi.
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/icons';
import './TrainingDetail.css';
import { colorFromName } from '../utils/colors';
import TrainingPilotPanel from '../components/telemetry/TrainingPilotPanel';
import TrajectoryExplorer from '../components/telemetry/TrajectoryExplorer';
import { formatLap } from '../components/telemetry/TelemetryCharts';
import { ThemeToggle } from '../components/ThemeContext';
import ReportDialog from '../components/ReportDialog';
import { stintPilotKey, fmtClock } from '../utils/crew';
import { useSession, canUse } from '../session';
import logoRaceSense from '../img/logo-racesense-live.svg';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);
const FULL_CACHE = 4;   // piloti con telemetria completa tenuti in memoria

const emptyLapBuffers = () => ({
  speed: [], accelX: [], accelY: [], accelZ: [],
  gLat: [], gLong: [], totalG: [], lateralG: [], brakeAccel: [],
  gyroX: [], gyroY: [], gyroZ: [],
  rpm: [], throttle: [], brake: [],
});

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
  } catch { return iso; }
};
const fmtDur = (s) => {
  if (s == null) return '—';
  const m = Math.floor(s / 60), sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

// Turno di un giro: dal giro stesso o dall'intervallo di giri del turno
const stintOf = (d, l) => l?.stint ?? (d?.stints || [])
  .find(s => s.lapFrom != null && Number(l?.lapNum) >= s.lapFrom && Number(l?.lapNum) <= s.lapTo)?.n;
const validLap = (l) => !l?.partial && Number(l?.lapTime) > 0;

// Piloti di un'auto, nell'ordine in cui sono saliti: turni, giri e migliore
function pilotsOf(d) {
  const out = [];
  for (const s of (Array.isArray(d?.stints) ? d.stints : [])) {
    const key = stintPilotKey(s);
    let p = out.find(x => x.key === key);
    if (!p) { p = { key, name: s.pilot?.name || 'Pilota', stints: [] }; out.push(p); }
    p.stints.push(s);
  }
  return out.map(p => {
    const bests = p.stints.map(s => Number(s.bestLapTime)).filter(v => v > 0);
    return {
      ...p,
      ns: p.stints.map(s => Number(s.n)),
      laps: p.stints.reduce((a, s) => a + (Number(s.laps) || 0), 0),
      best: bests.length ? Math.min(...bests) : null,
    };
  });
}

// "Chi ha guidato e quando": la sessione da inizio a fine, un tratto per turno
function StintStrip({ driver, start, end, selKey, onPick }) {
  const stints = (driver?.stints || []).filter(s => Number.isFinite(Number(s.from)));
  if (!stints.length) return null;
  const t0 = Math.min(Number(start) || Number(stints[0].from), Number(stints[0].from));
  const t1 = Math.max(Number(end) || 0, ...stints.map(s => Number(s.to) || Number(s.from)), t0 + 60000);
  const pct = (t) => `${((t - t0) / (t1 - t0)) * 100}%`;
  return (
    <div className="td-strip">
      <div className="td-strip__bar">
        {stints.map(s => {
          const key = stintPilotKey(s);
          const from = Number(s.from), to = Number(s.to) || t1;
          return (
            <button
              key={s.n}
              type="button"
              className={`td-strip__seg${key === selKey ? ' is-on' : ''}`}
              style={{ left: pct(from), width: `calc(${pct(to)} - ${pct(from)})`, '--seg': colorFromName(s.pilot?.name || key) }}
              onClick={() => onPick(key)}
              title={`${s.pilot?.name || 'Pilota'} · ${fmtClock(from)}–${fmtClock(to)} · ${s.laps || 0} giri`}
            >
              <span>{s.pilot?.name || 'Pilota'}</span>
            </button>
          );
        })}
      </div>
      <div className="td-strip__axis"><span>{fmtClock(t0)}</span><span>{fmtClock(t1)}</span></div>
    </div>
  );
}

export default function TrainingDetail({ training }) {
  const drivers = useMemo(() => training?.drivers || [], [training]);
  const withCars = drivers.some(d => d.car);
  const [selectedMac, setSelectedMac] = useState(drivers[0]?.mac || null);
  const [pilotSel, setPilotSel] = useState(null);          // pilota scelto sull'auto (null = il primo con giri)
  const lightPilot = useMemo(() => drivers.find(d => d.mac === selectedMac) || drivers[0] || null, [drivers, selectedMac]);
  const pickCar = (mac) => { setSelectedMac(mac); setPilotSel(null); };

  // Replay leggero (sessioni salvate): giri e traiettorie arrivano subito, la
  // telemetria completa di un'auto quando la si apre (pochi MB invece di
  // tutta la sessione).
  const lightMode = !!training?.light;
  const [fullDrivers, setFullDrivers] = useState({});        // mac → pilota completo
  const [loadError, setLoadError] = useState('');
  const [retry, setRetry] = useState(0);
  const pilotMac = lightPilot?.mac;
  const haveFull = !lightMode || !!fullDrivers[pilotMac];
  useEffect(() => {
    if (!lightMode || !pilotMac || fullDrivers[pilotMac]) return undefined;
    let alive = true;
    setLoadError('');
    fetch(`${API_BASE}/api/training/${encodeURIComponent(training.id)}/driver/${encodeURIComponent(pilotMac)}`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status === 404 ? 'Telemetria del pilota non trovata' : 'Telemetria non disponibile, riprova tra qualche istante'))))
      .then(d => {
        if (!alive || !d) return;
        setFullDrivers(prev => {
          const keep = Object.keys(prev).filter(k => k !== pilotMac).slice(-(FULL_CACHE - 1));
          return Object.fromEntries([...keep.map(k => [k, prev[k]]), [pilotMac, d]]);
        });
      })
      .catch(e => { if (alive) setLoadError(e.message); });
    return () => { alive = false; };
  }, [lightMode, pilotMac, fullDrivers, training?.id, retry]);
  const pilot = (lightMode ? fullDrivers[pilotMac] : null) || lightPilot;

  const laps = useMemo(() => pilot?.lapHistory || [], [pilot]);
  const lightLaps = useMemo(() => lightPilot?.lapHistory || [], [lightPilot]);

  // Auto con i cambi pilota: chi l'ha guidata e il pilota scelto
  const carPilots = useMemo(() => (lightPilot?.car ? pilotsOf(lightPilot) : []), [lightPilot]);
  const selPilot = carPilots.find(p => p.key === pilotSel) || carPilots.find(p => p.laps > 0) || carPilots[0] || null;
  const selKey = selPilot?.key ?? null;
  const ownLaps = useMemo(() => (selPilot ? lightLaps.filter(l => selPilot.ns.includes(Number(stintOf(lightPilot, l)))) : lightLaps),
    [lightLaps, selPilot, lightPilot]);
  const ownLapNums = useMemo(() => ownLaps.map(l => Number(l.lapNum)), [ownLaps]);
  const instrLap = lightLaps.find(l => lightPilot?.instructorLapNum != null && Number(l.lapNum) === Number(lightPilot.instructorLapNum)) || null;

  const [focusLapNum, setFocusLapNum] = useState(null);
  // report PDF: solo per chi è entrato nell'area operatore (non dal link condiviso)
  const session = useSession();
  const navigate = useNavigate();
  const canReport = !!session && canUse('training', session) && !!training?.id;
  const [reportFor, setReportFor] = useState(null);
  const [compareLapNum, setCompareLapNum] = useState(null);

  // default focus = miglior giro (o primo) del pilota scelto
  useEffect(() => {
    if (!ownLaps.length) { setFocusLapNum(null); setCompareLapNum(null); return; }
    // il migliore dell'allievo: il giro dell'istruttore è il riferimento, non il giro da analizzare
    const instr = lightPilot?.instructorLapNum;
    const own = ownLaps.filter(l => instr == null || Number(l.lapNum) !== Number(instr));
    const best = (own.length ? own : ownLaps).reduce((acc, l) => (l.lapTime && !l.partial && (!acc || l.lapTime < acc.lapTime) ? l : acc), null) || ownLaps[0];
    setFocusLapNum(best.lapNum);
    // confronto: giro istruttore, altrimenti l'ultimo giro pulito diverso dal
    // best (niente giro del cambio pilota né giri con sosta: oltre il 115% della mediana)
    const times = ownLaps.filter(validLap).map(l => l.lapTime).sort((x, y) => x - y);
    const median = times.length ? times[Math.floor(times.length / 2)] : null;
    const others = ownLaps.filter(l => l.lapNum !== best.lapNum && validLap(l) && !l.swap && (!median || l.lapTime <= median * 1.15));
    setCompareLapNum(lightPilot?.instructorLapNum ?? (others.length ? others[others.length - 1].lapNum : null));
  }, [ownLaps, lightPilot?.instructorLapNum]);

  const lapDataRef = useRef({ current: emptyLapBuffers(), currentDist: 0, currentLapNum: 0, prev: null, prevLapNum: null, lapHistory: [] });
  const telemBufferRef = useRef({ gLat: [], gLong: [] });
  const compareLapRef = useRef(null);
  const hoverRef = useRef({ dist: null });

  const selName = selPilot?.name || pilot?.fullName || pilot?.tag || '';
  const brandColor = pilot ? colorFromName(selPilot ? selPilot.name : (pilot.fullName || pilot.tag || pilot.mac)) : '#C0FF03';

  // aggiorna i ref statici alla selezione
  useEffect(() => {
    const focus = laps.find(l => l.lapNum === focusLapNum) || laps[laps.length - 1] || null;
    const compare = compareLapNum != null ? (laps.find(l => l.lapNum === compareLapNum) || null) : null;
    lapDataRef.current = {
      current: focus || emptyLapBuffers(),
      currentDist: focus?.totalDist || 0,
      currentLapNum: focus?.lapNum || 0,
      prev: compare,
      prevLapNum: compare?.lapNum ?? null,
      lapHistory: laps,
    };
    compareLapRef.current = compare;
    telemBufferRef.current = {
      gLat: (focus?.gLat || []).map(p => ({ value: p.value, ts: Date.now() })),
      gLong: (focus?.gLong || []).map(p => ({ value: p.value, ts: Date.now() })),
    };
  }, [laps, focusLapNum, compareLapNum]);

  const driverForPanel = useMemo(() => {
    if (!pilot) return null;
    const focus = laps.find(l => l.lapNum === focusLapNum);
    const lastSpeed = focus?.speed?.length ? focus.speed[focus.speed.length - 1].value : 0;
    return {
      mac: pilot.mac,
      // auto con più piloti: in testa il pilota scelto, l'auto accanto
      fullName: selPilot ? selPilot.name : pilot.fullName,
      tag: pilot.tag,
      team: selPilot ? (pilot.car?.name || pilot.fullName) : pilot.team,
      formula: pilot.formula,
      lapCount: pilot.lapCount, lastLapTime: pilot.lastLapTime, bestLapTime: pilot.bestLapTime,
      speedKmh: lastSpeed, gforce: null,
      sats: null, battery: null, signal4g: null, mcuTemp: null, modemTemp: null,
      // Replay: nessun battito live; mostriamo la media di sessione (se il pilota aveva la fascia)
      hr: pilot.hrAvg ?? null, beaconId: null, hrCaption: pilot.hrAvg != null ? 'media sessione' : null, hrMax: pilot.hrMax ?? null, hrAvg: pilot.hrAvg ?? null,
      cornerTimes: pilot.cornerTimes || {}, bestCornerTimes: pilot.bestCornerTimes || {},
      lapLabels: pilot.lapLabels || {},
      obd: null,
      car: pilot.car || null, pilot: pilot.pilot || null, stints: pilot.stints || null,
    };
  }, [pilot, laps, focusLapNum, selPilot]);

  // Confronto sulla mappa: il miglior giro degli altri piloti (anche sulla stessa auto)
  const compareOptions = useMemo(() => {
    const out = [];
    for (const d of drivers) {
      const groups = d.car ? pilotsOf(d) : [{ key: null, name: d.fullName || d.tag || d.mac, ns: null }];
      for (const g of groups) {
        if (d.mac === pilotMac && (!d.car || g.key === selKey)) continue;
        const best = (d.lapHistory || [])
          .filter(l => validLap(l) && Number(l.lapNum) !== Number(d.instructorLapNum) && (!g.ns || g.ns.includes(Number(stintOf(d, l)))))
          .reduce((b, l) => (!b || l.lapTime < b.lapTime ? l : b), null);
        if (!best) continue;
        const name = d.car && d.mac !== pilotMac ? `${g.name} · ${d.car.name || d.fullName}` : g.name;
        out.push({ key: `${d.mac}|${g.key ?? ''}`, name, color: colorFromName(`${g.name}${d.mac}`), lap: best });
      }
    }
    return out;
  }, [drivers, pilotMac, selKey]);

  if (!training) return null;

  // Sessione: auto, piloti e miglior giro (con chi l'ha fatto)
  const pilotsCount = new Set(drivers.flatMap(d => (d.car ? pilotsOf(d).map(p => p.key) : [d.mac]))).size;
  let sessionBest = null;
  for (const d of drivers) {
    for (const l of (d.lapHistory || [])) {
      if (!validLap(l) || Number(l.lapNum) === Number(d.instructorLapNum)) continue;
      if (!sessionBest || l.lapTime < sessionBest.t) {
        const who = d.car ? (d.stints || []).find(s => s.n === stintOf(d, l))?.pilot?.name : (d.fullName || d.tag);
        sessionBest = { t: l.lapTime, who: who || d.fullName || d.tag };
      }
    }
  }
  const hasOwnLaps = ownLaps.some(validLap);
  const openReport = () => setReportFor({
    mac: lightPilot.mac,
    name: lightPilot.car ? (lightPilot.car.name || lightPilot.fullName) : (lightPilot.fullName || lightPilot.tag),
    stints: lightPilot.stints || null,
    initial: selPilot ? selPilot.ns : null,
  });
  const reportButton = canReport && hasOwnLaps && (
    <button type="button" className="rs-btn rs-btn--primary rs-btn--sm td-pick__pdf" onClick={openReport} title={`Report PDF di ${selName}`}>
      <Icon.Download size={14} /> Report PDF di {selName}
    </button>
  );

  return (
    <div className="rs-live-root td-root">
      <div className="td-bar">
        <div className="td-bar__left">
          {/* chi è entrato nell'area operatore torna all'archivio; chi ha solo il link no */}
          {session && (
            <button type="button" className="rs-btn rs-btn--sm rs-btn--ghost td-back" onClick={() => navigate('/allenamenti-passati')}>
              <Icon.ArrowLeft size={15} /><span>Allenamenti passati</span>
            </button>
          )}
          <span className="td-bar__brand"><img src={logoRaceSense} alt="RaceSense Live" className="td-bar__logo" /></span>
          <span className="td-bar__replay"><Icon.Clock /> Replay allenamento</span>
        </div>
        <ThemeToggle />
      </div>

      <header className="rs-page-header td-header">
        <div className="rs-page-header__text">
          <span className="rs-kicker rs-kicker--lime"><span className="rs-kicker__dot rs-kicker__dot--lime" />Sessione di allenamento</span>
          <h1 className="rs-page-header__title">{training.circuit?.name || 'Circuito'}</h1>
          <div className="td-header__meta">
            <span>{fmtDate(training.startTime)}</span>
            <span>Durata {fmtDur(training.durationSeconds)}</span>
            {withCars
              ? <span>{drivers.length} auto · {pilotsCount} {pilotsCount === 1 ? 'pilota' : 'piloti'}</span>
              : <span>{drivers.length} {drivers.length === 1 ? 'pilota' : 'piloti'}</span>}
            {sessionBest && <span>Miglior giro <b className="td-best">{formatLap(sessionBest.t)}</b> · {sessionBest.who}</span>}
          </div>
        </div>
      </header>

      {/* ═══ SCELTA: auto e pilota (o pilota, senza auto) ═══ */}
      <section className="rs-card td-pick" aria-label="Scegli cosa analizzare">
        <div className="td-pick__row">
          <span className="td-pick__label">{withCars ? '1 · Auto' : 'Pilota'}</span>
          <div className="td-pick__list">
            {drivers.map(d => {
              const active = d.mac === pilotMac;
              const n = d.car ? pilotsOf(d).length : 0;
              const img = d.car?.photoUrl ? `${API_BASE}${d.car.photoUrl}` : null;
              return (
                <button key={d.mac} type="button" className={`td-tile ${active ? 'is-active' : ''}`} style={{ '--tile': colorFromName(d.fullName || d.tag || d.mac) }}
                  onClick={() => pickCar(d.mac)} aria-pressed={active}>
                  {d.car
                    ? (img ? <img className="td-tile__img" src={img} alt="" /> : <span className="td-tile__img td-tile__img--ph"><Icon.Car size={16} /></span>)
                    : <span className="td-tile__dot" />}
                  <span className="td-tile__text">
                    <strong>{d.fullName || d.tag}</strong>
                    <small>{d.car ? `${n} ${n === 1 ? 'pilota' : 'piloti'} · ` : ''}{d.lapCount || 0} giri{!d.car ? ` · ${formatLap(d.bestLapTime)}` : ''}</small>
                  </span>
                </button>
              );
            })}
          </div>
          {!withCars && reportButton}
        </div>

        {withCars && lightPilot?.car && (
          <>
            <div className="td-pick__row">
              <span className="td-pick__label">2 · Pilota</span>
              <div className="td-pick__list">
                {carPilots.map(p => {
                  const active = p.key === selKey;
                  return (
                    <button key={p.key} type="button" className={`td-tile td-tile--pilot ${active ? 'is-active' : ''}`} style={{ '--tile': colorFromName(p.name) }}
                      onClick={() => setPilotSel(p.key)} aria-pressed={active} disabled={!p.laps && !active}
                      title={p.laps ? `Analisi di ${p.name}` : `${p.name} non ha giri completi`}>
                      <span className="td-tile__dot" />
                      <span className="td-tile__text">
                        <strong>{p.name}</strong>
                        <small>{p.laps} {p.laps === 1 ? 'giro' : 'giri'}{p.best ? ` · best ${formatLap(p.best)}` : ''}</small>
                        <small className="td-tile__when">{p.stints.map(s => `${fmtClock(s.from)}–${fmtClock(s.to)}`).join(' · ')}</small>
                      </span>
                    </button>
                  );
                })}
              </div>
              {reportButton}
            </div>
            <div className="td-pick__row td-pick__row--strip">
              <span className="td-pick__label">Chi ha guidato</span>
              <StintStrip driver={lightPilot} start={Date.parse(training.startTime)} end={Date.parse(training.endTime)} selKey={selKey} onPick={setPilotSel} />
            </div>
          </>
        )}
      </section>

      {pilot && (
        <>
          <div className="td-focus">
            <span className="td-focus__swatch" style={{ background: brandColor }} />
            <h2>{selName}</h2>
            {lightPilot?.car && <span>{lightPilot.car.name || lightPilot.fullName}</span>}
            <span>{ownLaps.filter(validLap).length} giri completi</span>
          </div>

          <section className="td-layout">
            <div className="rs-card">
              <div className="rs-card__head">
                <span className="rs-card__title rs-card__title--lime"><Icon.Track /> Traiettorie<span className="lv-card__sub">Giro {focusLapNum ?? '—'} · colore = velocità</span></span>
              </div>
              <TrajectoryExplorer
                circuit={training.circuit}
                drivers={drivers}
                pilotMac={pilotMac}
                focusLapNum={focusLapNum}
                compareLapNum={compareLapNum}
                instructorLapNum={lightPilot?.instructorLapNum}
                lapNums={selPilot ? ownLapNums : null}
                compareOptions={compareOptions}
                resetKey={selKey}
              />
            </div>
            <div className="rs-card">
              <div className="rs-card__head"><span className="rs-card__title"><Icon.Layers /> Giri di {selName}</span></div>
              <div className="rs-card__body td-controls">
                <div className="rs-field">
                  <label className="rs-label">Giro analizzato</label>
                  <select className="rs-select" value={focusLapNum ?? ''} onChange={e => setFocusLapNum(Number(e.target.value))} disabled={!ownLaps.length}>
                    {ownLaps.map(l => <option key={l.lapNum} value={l.lapNum}>LAP {l.lapNum}{l.partial ? ' (parz.)' : ''} {l.lapTime ? `— ${formatLap(l.lapTime)}` : ''}</option>)}
                  </select>
                </div>
                <div className="rs-field">
                  <label className="rs-label">Confronto (ghost)</label>
                  <select className="rs-select" value={compareLapNum ?? ''} onChange={e => setCompareLapNum(e.target.value === '' ? null : Number(e.target.value))}>
                    <option value="">Nessuno</option>
                    {instrLap && !ownLaps.includes(instrLap) && <option value={instrLap.lapNum}>Istruttore · LAP {instrLap.lapNum} — {formatLap(instrLap.lapTime)}</option>}
                    {ownLaps.filter(l => l.lapNum !== focusLapNum).map(l => <option key={l.lapNum} value={l.lapNum}>LAP {l.lapNum} {l.lapTime ? `— ${formatLap(l.lapTime)}` : ''}</option>)}
                  </select>
                </div>
                <p className="td-controls__note">
                  Il giro analizzato è colorato per velocità; il confronto è in bianco{lightPilot?.instructorLapNum != null ? ', il giro dell\'istruttore in azzurro' : ''}.
                  Con «Confronta con» sotto la mappa aggiungi il miglior giro di un altro pilota.
                </p>
              </div>
            </div>
          </section>

          {/* Telemetria dettagliata (replay leggero: arriva quando si apre l'auto) */}
          {!haveFull ? (
            <div className="rs-card td-loading">
              {loadError
                ? <div className="rs-alert rs-alert--danger"><Icon.Alert /><span>{loadError}</span><button type="button" className="rs-btn rs-btn--sm" onClick={() => setRetry(n => n + 1)}>Riprova</button></div>
                : <span><span className="rs-spinner" /> Caricamento della telemetria di {selName}…</span>}
            </div>
          ) : (
          <TrainingPilotPanel
            driver={driverForPanel}
            lapHistory={laps}
            compareLapNum={compareLapNum}
            onChangeCompareLap={setCompareLapNum}
            lapDataRef={lapDataRef}
            telemetryBufferRef={telemBufferRef}
            compareLapRef={compareLapRef}
            hoverRef={hoverRef}
            brandColor={brandColor}
            replay
            ggStatic
            focusLapNum={focusLapNum}
            instructorLapNum={pilot.instructorLapNum}
            instructorTrailMode={pilot.instructorTrailMode}
            pilotKey={lightPilot?.car ? (selKey ?? 'all') : undefined}
          />
          )}
        </>
      )}

      {reportFor && (
        <ReportDialog
          kind="training"
          endpoint={`/api/training/recordings/${encodeURIComponent(training.id)}/report/${encodeURIComponent(reportFor.mac)}`}
          name={reportFor.name}
          stints={reportFor.stints}
          initialStints={reportFor.initial}
          onClose={() => setReportFor(null)}
        />
      )}
    </div>
  );
}
